import { RewardType } from 'root/src/shared/rewards/constants';
import { dropKeyPlatformRegionsOptionsMap, readableKeyTypesMap, readablePlatformsMap, } from 'root/src/shared/constants/dropKeys';
import { capitalize } from 'root/src/shared/util/stringCase';
const isProductType = (prefix) => {
    return prefix === RewardType.CODE || prefix === RewardType.KEY || prefix === RewardType.BUNDLE || prefix === RewardType.CUSTOM;
};
class KeyProductResolver {
    constructor(sortKey) {
        const [, state, dropGameId, keyPlatform, keyRegion, keyType, encryptData] = sortKey.split('|');
        if (!state || !dropGameId || !keyPlatform || !keyRegion || !keyType || !encryptData) {
            throw new Error(`Invalid sortKey: ${sortKey}`, { cause: sortKey });
        }
        this._state = state;
        this._platform = keyPlatform;
        this._region = keyRegion;
        this._type = keyType;
        this._dropGameId = dropGameId;
        this._encryptData = encryptData;
    }
    get state() {
        return this._state;
    }
    get platform() {
        return this._platform;
    }
    get region() {
        return this._region;
    }
    get type() {
        return this._type;
    }
    get dropGameId() {
        return this._dropGameId;
    }
    get productId() {
        return `${this.dropGameId}|${this.platform}|${this.region}|${this.type}`;
    }
    get encryptedData() {
        return this._encryptData;
    }
}
export class KeyProductMnemonicIdResolver {
    constructor(mnemonicId) {
        const [dropGameId, platform, region, type] = mnemonicId.split('|');
        if (!dropGameId || !platform || !region || !type) {
            throw new Error(`Invalid mnemonicId: ${mnemonicId}`, { cause: mnemonicId });
        }
        this._region = region;
        this._platform = platform;
        this._type = type;
        this._dropGameId = dropGameId;
    }
    get region() {
        return this._region;
    }
    get type() {
        return this._type;
    }
    get dropGameId() {
        return this._dropGameId;
    }
    get platform() {
        return this._platform;
    }
    getReadablePlatform() {
        return readablePlatformsMap[this.platform];
    }
    getReadableKeyType() {
        return readableKeyTypesMap[this._type];
    }
    getReadableRegion() {
        const option = dropKeyPlatformRegionsOptionsMap[this.platform];
        const regionOption = option === null || option === void 0 ? void 0 : option.find(({ value }) => value === this.region);
        if (!regionOption) {
            return capitalize(this.region);
        }
        return regionOption.label;
    }
}
class BundleProductResolver {
    constructor(sortKey) {
        const [, state, id] = sortKey.split('|');
        if (!state || !id) {
            throw new Error('Invalid bundle sortKey', { cause: sortKey });
        }
        this._productId = id;
        this._state = state;
    }
    get productId() {
        return this._productId;
    }
    get state() {
        return this._state;
    }
}
class CodeProductResolver {
    constructor(sortKey) {
        const [, state, codeId, encryptedData] = sortKey.split('|');
        if (!state || !codeId || !encryptedData) {
            throw new Error('Invalid sortKey', { cause: sortKey });
        }
        this._state = state;
        this._codeId = codeId;
        this._encryptedData = encryptedData;
    }
    get state() {
        return this._state;
    }
    get codeId() {
        return this._codeId;
    }
    get productId() {
        return this.codeId;
    }
    get encryptedData() {
        return this._encryptedData;
    }
}
class CustomProductResolver {
    constructor(sortKey) {
        const [, id] = sortKey.split('|');
        if (!id) {
            throw new Error('Invalid sortKey', { cause: sortKey });
        }
        this._customProductId = id;
    }
    get productId() {
        return this._customProductId;
    }
}
export class ProductResolver {
    static resolveState(sortKey) {
        const prefix = sortKey.split('|').at(0);
        if (!isProductType(prefix)) {
            return undefined;
        }
        switch (prefix) {
            case RewardType.KEY:
                return new KeyProductResolver(sortKey).state;
            case RewardType.CODE:
                return new CodeProductResolver(sortKey).state;
            default:
                return undefined;
        }
    }
    static resolveProductId(sortKey) {
        const prefix = sortKey.split('|').at(0);
        if (!isProductType(prefix)) {
            return undefined;
        }
        switch (prefix) {
            case RewardType.KEY:
                return new KeyProductResolver(sortKey).productId;
            case RewardType.CODE:
                return new CodeProductResolver(sortKey).productId;
            case RewardType.CUSTOM:
                return new CustomProductResolver(sortKey).productId;
            case RewardType.BUNDLE:
                return new BundleProductResolver(sortKey).productId;
            default:
                return undefined;
        }
    }
    static resolveType(sortKey) {
        const prefix = sortKey.split('|').at(0);
        if (!isProductType(prefix)) {
            return undefined;
        }
        return prefix;
    }
    static resolveEncryptedData(sortKey) {
        const prefix = sortKey.split('|').at(0);
        if (!isProductType(prefix)) {
            return undefined;
        }
        switch (prefix) {
            case RewardType.KEY:
                return new KeyProductResolver(sortKey).encryptedData;
            case RewardType.CODE:
                return new CodeProductResolver(sortKey).encryptedData;
            default:
                return undefined;
        }
    }
    static resolveRegionByMnemonicId(mnemonicId, type) {
        if (!isProductType(type)) {
            return undefined;
        }
        switch (type) {
            case RewardType.KEY:
                return new KeyProductMnemonicIdResolver(mnemonicId).region;
            default:
                return undefined;
        }
    }
    static resolvePlatformByMnemonicId(mnemonicId, type) {
        if (!isProductType(type)) {
            return undefined;
        }
        switch (type) {
            case RewardType.KEY:
                return new KeyProductMnemonicIdResolver(mnemonicId).platform;
            default:
                return undefined;
        }
    }
}
